import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch } from 'react-redux'
import { formatDNI } from '@chilecompra/react-kit/utils'
import { Input, useInput, putCookie, Typography } from '@chilecompra/react-kit'
import { useAnalyticsEvent, useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Button, CheckBox, useCheckBox, useViewport } from '@chilecompra/react-kit/components'
import { onRegisterBy } from './RegisterWizard.actions'
import { onGetUserInfoThunk, onSignInUniquePassThunk } from '../UniquePassAuth/UniquePassAuth.actions'
import {
  isLocalEnv,
  REACT_APP_API_BASE_URL,
  REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED,
  REACT_APP_UNIQUE_PASS_SEED
} from '../../config/settings/environment'
import { useCsrfToken } from '../../modules/hooks/security'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  Conditions,
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  MobileNavigation,
  Root,
  Section,
  TermsLink,
  Title
} from './RegisterWizardUniquePass.styles'

/**
 * The RegisterWizardUniquePass's container.
 */
const RegisterWizardUniquePass = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { value: checked, onChange: handleChange } = useCheckBox()
  const { value: dniValue, onChange: handleChangeDniValue } = useInput({
    initialValue: '',
    formatCallbacks: [formatDNI]
  })
  const { size } = useViewport()
  const csrfToken = useCsrfToken(REACT_APP_UNIQUE_PASS_SEED)
  const claveUnicaUri = `${REACT_APP_API_BASE_URL}/v1/registro-civil/clave-unica/sso/authorize?csrf=${csrfToken}&aplicativo=inscripcion-proveedor`
  const dniTestFeatureEnabled =
    REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === true || REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === 'true'

  const handleBack = () => onChangeStep('welcome')

  const renderClaveUnicaSection = () => {
    return (
      <Typography tabIndex="0" variant="body1" fontSize="14px" marginTop="15px" fontWeight="bold" textAlign="left">
        Para comenzar el proceso de registro ingresa con tu Clave Única:
      </Typography>
    )
  }

  const renderTestInput = () => {
    return (
      <Input
        value={dniValue}
        onChange={handleChangeDniValue}
        margin="15px 0 0 0"
        placeholder="Ingresa RUT para testear"
        aria-label="Rut test"
      />
    )
  }

  const handleNext = useCallback(() => {
    if (!isLocalEnv) {
      putCookie('dniTestCookie', dniValue, { base64: true, json: true })
      dispatch(onSignInUniquePassThunk({ csrfToken }))
    } else {
      handleRegisterByUniquePass()
    }
  }, [dniValue, isLocalEnv, onChangeStep, claveUnicaUri])

  const disabledUniqueKey = !isLocalEnv && !checked

  useAnalyticsEvent({ action: 'Inicia registro por ClaveÚnica', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/1/clave_unica')

  const handleRegisterByUniquePass = () => {
    if (dniTestFeatureEnabled) {
      putCookie('dniTestCookie', dniValue, { base64: true, json: true })
      batch(() => {
        dispatch(onGetUserInfoThunk({ token: csrfToken }))
        dispatch(onRegisterBy('by-unique-pass'))
      })
    }
    onChangeStep('personal-info')
  }

  return (
    <Root>
      <Section container>
        <Section item xs={12} md={6} maxWidth="546px">
          <Title>Comencemos</Title>
          <Instructions>
            Acepta los términos y condiciones y haz click en el botón de ClaveÚnica para poder acceder al portal del
            Registro Civil
          </Instructions>
          <Conditions>
            <CheckBox checked={checked} onChange={handleChange} color="primary" />
            Acepto{' '}
            <TermsLink
              href="https://www.chilecompra.cl/terminos-y-condiciones-de-uso/"
              rel="noopener noreferrer"
              target="_blank"
            >
              términos y condiciones de uso
            </TermsLink>
          </Conditions>
        </Section>
        <Hidden smDown>
          <Section item xs={12} sm={6} margin="-35px 0 0" padding="0 0 0 51px">
            <Image alt="Registro vía ClaveÚnica" src="/assets/img/steps/paso1.svg?h=e57104cc1427561c7d89c6004abefe06" />
          </Section>
        </Hidden>
      </Section>
      {!size.isTiny && (
        <Section container margin="52px 0 0" maxWidth="420px">
          <ProtectByFeatures rule={features => features['com.seller/dni.test.input']}>
            {renderClaveUnicaSection()}
            {renderTestInput()}
          </ProtectByFeatures>
          <DesktopNavigation>
            <Button onClick={handleBack} type="button" variant="text" width="100%">
              Cancelar
            </Button>
            <Button
              color="primary"
              disabled={disabledUniqueKey}
              margin="0 0 0 30px"
              type="button"
              width="100%"
              onClick={handleNext}
            >
              <Image alt="Ir a ClaveÚnica" height="20px" src="/assets/img/logo-clave-unica.svg" />
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation dniTestFeatureEnabled={dniTestFeatureEnabled}>
          <Section container margin="0 20px">
            <ProtectByFeatures rule={features => features['com.seller/dni.test.input']}>
              <Section item xs={12} margin="0 120px">
                {renderTestInput()}
              </Section>
            </ProtectByFeatures>
            <Section item xs={6}>
              <Button onClick={handleBack} type="button" variant="text" width="168px">
                Cancelar
              </Button>
            </Section>
            <Section item xs={6}>
              <Button
                color="primary"
                disabled={disabledUniqueKey}
                margin="0 0 0 30px"
                type="button"
                width="168px"
                onClick={handleNext}
              >
                <Image alt="Ir a ClaveÚnica" height="20px" src="/assets/img/logo-clave-unica.svg" />
              </Button>
            </Section>
          </Section>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardUniquePass.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardUniquePass.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardUniquePass
